import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar, MobileNav } from "@/components/Navbar";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CompBadge } from "@/components/OpportunityCard";
import { ShareButton } from "@/components/ShareButton";
import { AddToCalendar } from "@/components/AddToCalendar";
import { EventContentSubmit } from "@/components/EventContentSubmit";
import { MatchBadge, MatchReasons } from "@/components/MatchBadge";
import { BusinessVerifiedSocials } from "@/components/BusinessVerifiedSocials";
import { opportunityShareUrl } from "@/lib/share";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MediaKitPreview } from "@/components/mediakit/MediaKitPreview";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { MapPin, Loader2, ArrowLeft, CheckCircle2, Send, Target, X, Ticket, Clock, Calendar, Users, FileText, Eye, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function OpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [o, setO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [mk, setMk] = useState(null);          // { is_pro, has_kit, kit }
  const [includeMk, setIncludeMk] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const draftKey = user ? `nf_appdraft_${user.id}_${id}` : null;

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/opportunities/${id}`);
      setO(r.data);
    } catch { setO(null); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Creator-only: fetch media-kit attach eligibility once.
  useEffect(() => {
    if (user?.role === "creator") {
      api.get("/creator/media-kit/attachable").then((r) => setMk(r.data)).catch(() => setMk({ is_pro: false, has_kit: false, kit: null }));
    }
  }, [user]);

  // Restore a preserved application draft (e.g. after leaving to create a media kit).
  useEffect(() => {
    if (!draftKey) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const d = JSON.parse(raw);
        if (typeof d.msg === "string") setMsg(d.msg);
        if (d.includeMk) setIncludeMk(true);
        if (d.open) setOpen(true);
      }
    } catch { /* */ }
  }, [draftKey]);

  const goCreateKit = () => {
    try { if (draftKey) localStorage.setItem(draftKey, JSON.stringify({ msg, includeMk: true, open: true })); } catch { /* */ }
    navigate("/creator/media-kit");
  };
  const discardDraft = () => { try { if (draftKey) localStorage.removeItem(draftKey); } catch { /* */ } };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/opportunities/${id}/apply`, { message: msg, include_media_kit: includeMk && !!mk?.is_pro && !!mk?.has_kit });
      setOpen(false);
      discardDraft();
      toast.success("Application sent 🎉");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const respondInvite = async (status) => {
    setBusy(true);
    try {
      const r = await api.post(`/applications/${o.my_application_id}/respond`, { status });
      toast.success(status === "accepted" ? "Invitation accepted 🎉" : "Invitation declined");
      if (status === "accepted" && r.data.collaboration_id) navigate(`/collaborations/${r.data.collaboration_id}`);
      else load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  if (!o) return (
    <div className="min-h-screen bg-[#FAF9F6] nf-pb-nav">
      <Navbar />
      <div className="mx-auto max-w-lg px-4 py-20 text-center sm:py-28">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-zinc-900 text-white"><Target className="h-6 w-6" /></div>
        <p className="mt-4 font-display text-xl font-bold text-zinc-800">This opportunity isn't available</p>
        <p className="mt-1 text-sm text-zinc-500">It may have been closed, filled or removed. Plenty of other local opportunities are waiting for you.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <Button data-testid="opp-notfound-browse" onClick={() => navigate("/opportunities")} className="rounded-full bg-emerald-600 hover:bg-emerald-700">Browse opportunities</Button>
          <Button data-testid="opp-notfound-dashboard" variant="outline" onClick={() => navigate("/dashboard")} className="rounded-full">Back to dashboard</Button>
        </div>
      </div>
      <MobileNav />
    </div>
  );

  const isCreator = user?.role === "creator";
  const isInvited = o.my_application_type === "invitation" && o.my_application_status === "invited";
  const hasApplied = o.has_applied && !isInvited;
  const dl = o.deliverables_list && o.deliverables_list.length ? o.deliverables_list.map((x) => `${x.quantity} ${x.type}`).join(" + ") : o.deliverables;
  const Row = ({ label, value }) => value ? <div className="flex justify-between gap-4 border-b border-zinc-100 py-2 text-sm"><span className="text-zinc-500">{label}</span><span className="text-right font-medium text-zinc-900">{value}</span></div> : null;

  return (
    <div className="min-h-screen bg-[#FAF9F6] nf-pb-action">
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-4 flex items-center justify-between">
          <button onClick={() => navigate("/opportunities")} className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900"><ArrowLeft className="h-4 w-4" /> Back to opportunities</button>
          <ShareButton testid="opp-detail-share" url={opportunityShareUrl(id)} title={o.title}
            text={`${o.title} — ${o.business_name}, ${o.location}. Apply on Nearfluence.`} recordKind="opportunity" recordId={id} label="Share" />
        </div>

        {isInvited && (
          <div data-testid="invitation-banner" className="mb-4 flex items-center gap-3 rounded-2xl border border-sky-200 bg-sky-50 p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-100 text-sky-600"><Target className="h-5 w-5" /></div>
            <div>
              <p className="font-display font-bold text-sky-900">You've been invited 🎯</p>
              <p className="text-sm text-sky-700">{o.business_name} thinks you'd be a great fit — no application needed.</p>
            </div>
          </div>
        )}

        <div className="overflow-hidden rounded-3xl border border-zinc-200 bg-white">
          {o.cover_image && <div className="h-48 w-full overflow-hidden bg-zinc-100"><img src={fileUrl(o.cover_image)} alt="" className="h-full w-full object-cover" /></div>}
          <div className="p-6">
            <div className="flex items-center gap-3">
              <div className="h-14 w-14 overflow-hidden rounded-xl bg-zinc-100">
                {o.business_logo ? <img src={fileUrl(o.business_logo)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center font-display font-bold text-zinc-400">{o.business_name?.[0]}</div>}
              </div>
              <div className="min-w-0 flex-1">
                <button data-testid="opp-business-link" onClick={() => navigate(`/business/${o.business_user_id}`)} className="text-sm font-medium text-zinc-500 hover:text-emerald-700 hover:underline">{o.business_name}</button>
                <p className="flex items-center gap-1 text-xs text-zinc-400"><MapPin className="h-3 w-3" /> {o.location}{o.distance_km != null && <span className="font-semibold text-emerald-600"> · {o.distance_km} km</span>}</p>
              </div>
              <CompBadge type={o.compensation_type} />
            </div>

            {o.business_verified_socials?.length > 0 && (
              <BusinessVerifiedSocials socials={o.business_verified_socials} testid="opp-business-verified-socials" className="mt-3" showPosts />
            )}

            <h1 className="mt-4 font-display text-2xl font-bold text-zinc-900">{o.title}</h1>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {o.category && <span className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs text-zinc-600">{o.category}</span>}
              {o.niches?.map((n) => <span key={n} className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs text-emerald-700">{n}</span>)}
              {o.platforms?.map((p) => <span key={p} className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs capitalize text-zinc-600">{p}</span>)}
            </div>

            {o.description && <p className="mt-4 whitespace-pre-line text-zinc-600">{o.description}</p>}

            {o.opp_type === "event" && (
              <div data-testid="event-detail-block" className="mt-4 rounded-2xl border border-fuchsia-200 bg-fuchsia-50/60 p-4">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1 rounded-full bg-fuchsia-600 px-2.5 py-0.5 text-xs font-bold text-white"><Ticket className="h-3.5 w-3.5" /> EVENT</span>
                  {o.event_status === "cancelled" ? <span className="text-xs font-semibold text-rose-600">Cancelled</span>
                    : o.event_status === "completed" ? <span className="text-xs font-semibold text-zinc-500">Completed</span>
                    : o.is_full ? <span data-testid="event-full-tag" className="text-xs font-semibold text-amber-600">Event full{o.enable_waitlist ? " · waitlist open" : ""}</span>
                    : <span data-testid="event-spaces-tag" className="text-xs font-semibold text-fuchsia-700">{o.spaces_remaining} space{o.spaces_remaining === 1 ? "" : "s"} remaining</span>}
                </div>
                <div className="mt-3 grid grid-cols-1 gap-1.5 text-sm sm:grid-cols-2">
                  {o.venue_name && <p className="flex items-center gap-1.5 text-zinc-700"><MapPin className="h-4 w-4 text-fuchsia-600" /> {o.venue_name}</p>}
                  {o.event_date && <p className="flex items-center gap-1.5 text-zinc-700"><Calendar className="h-4 w-4 text-fuchsia-600" /> {o.event_date}</p>}
                  {(o.start_time || o.end_time) && <p className="flex items-center gap-1.5 text-zinc-700"><Clock className="h-4 w-4 text-fuchsia-600" /> {o.start_time}{o.end_time ? `–${o.end_time}` : ""}</p>}
                  {o.plus_one_allowed && <p className="flex items-center gap-1.5 text-zinc-700"><Users className="h-4 w-4 text-fuchsia-600" /> Plus-one allowed</p>}
                </div>
                {o.what_creators_receive && <p className="mt-2 text-sm text-zinc-700"><span className="font-semibold">You'll receive:</span> {o.what_creators_receive}</p>}
                {o.complimentary && <p className="mt-1 text-sm text-zinc-700"><span className="font-semibold">Complimentary:</span> {o.complimentary}</p>}
                {o.content_expectations && <p className="mt-1 text-sm text-zinc-700"><span className="font-semibold">Content expectations:</span> {o.content_expectations}</p>}
                {o.event_status !== "cancelled" && o.event_status !== "completed" && (
                  <div className="mt-3">
                    <AddToCalendar opportunity={o} testid="opp-detail-add-calendar" />
                  </div>
                )}
              </div>
            )}

            {o.opp_type === "event" && o.can_submit_content && (
              <EventContentSubmit opportunityId={id} initial={o.my_content || []} />
            )}

            {o.match && (
              <div className="mt-5">
                <MatchReasons match={o.match} testid="opp-detail-match" />
              </div>
            )}

            <div className="mt-5">
              <Row label="Deliverables" value={dl} />
              <Row label="Payment" value={o.compensation_type !== "gifted" && o.payment_amount != null ? `£${o.payment_amount}` : null} />
              <Row label="Gift / reward" value={o.gift_description} />
              <Row label="Creators required" value={String(o.creators_needed)} />
              <Row label="Min followers" value={o.min_followers ? o.min_followers.toLocaleString() : null} />
              <Row label="Application deadline" value={o.deadline} />
              <Row label="Campaign date" value={o.campaign_date} />
              <Row label="Content deadline" value={o.content_deadline} />
              <Row label="Additional requirements" value={o.additional_requirements} />
            </div>
          </div>
        </div>
      </div>

      {isCreator && (
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="right" className="flex w-full flex-col overflow-y-auto sm:max-w-md">
            <SheetHeader>
              <SheetTitle>Apply for this opportunity</SheetTitle>
              <SheetDescription>Your Nearfluence profile (stats, portfolio, niches) is sent automatically — no need to re-enter anything.</SheetDescription>
            </SheetHeader>
            <div className="mt-4">
              <label className="text-sm font-medium text-zinc-700">Why do you think you'd be a good fit? <span className="text-zinc-400">(optional)</span></label>
              <Textarea data-testid="apply-message" rows={5} maxLength={500} value={msg} onChange={(e) => setMsg(e.target.value)} className="mt-1.5" placeholder="A short note to the business…" />
              <p className="mt-1 text-right text-xs text-zinc-400">{msg.length}/500</p>

              {/* Creator Pro: attach media kit */}
              {mk?.is_pro && mk?.has_kit && (
                <div data-testid="media-kit-attach-block" className="mt-2 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-3">
                  <label className="flex items-start gap-2.5">
                    <Checkbox data-testid="include-media-kit" checked={includeMk} onCheckedChange={(v) => setIncludeMk(!!v)} className="mt-0.5" />
                    <span className="text-sm">
                      <span className="flex items-center gap-1.5 font-medium text-zinc-800"><FileText className="h-4 w-4 text-emerald-600" /> Include my media kit</span>
                      <span className="text-xs text-zinc-500">Attach a private snapshot of your saved media kit to this application.</span>
                    </span>
                  </label>
                  <Button type="button" variant="outline" size="sm" data-testid="preview-media-kit" onClick={() => setPreviewOpen(true)} className="mt-2 rounded-full">
                    <Eye className="mr-1.5 h-3.5 w-3.5" /> Preview media kit
                  </Button>
                </div>
              )}
              {mk?.is_pro && !mk?.has_kit && (
                <div data-testid="media-kit-create-block" className="mt-2 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-3">
                  <p className="flex items-center gap-1.5 text-sm font-medium text-zinc-800"><Sparkles className="h-4 w-4 text-emerald-600" /> Stand out with your media kit</p>
                  <p className="mt-0.5 text-xs text-zinc-500">You don't have a saved media kit yet. Create one and we'll keep this application draft for you.</p>
                  <Button type="button" size="sm" data-testid="create-media-kit" onClick={goCreateKit} className="mt-2 rounded-full bg-emerald-600 hover:bg-emerald-700">
                    <FileText className="mr-1.5 h-3.5 w-3.5" /> Create your media kit
                  </Button>
                </div>
              )}

              <Button data-testid="send-application" onClick={submit} disabled={busy} className="mt-3 h-11 w-full rounded-full bg-emerald-600 hover:bg-emerald-700">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Send application</>}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Media kit preview modal (same page — creator keeps their draft) */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent data-testid="media-kit-preview-modal" className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Media kit preview</DialogTitle>
            <DialogDescription>A preview of the media kit snapshot that will be attached to your application.</DialogDescription>
          </DialogHeader>
          {mk?.kit ? <MediaKitPreview kit={mk.kit} /> : <p className="text-sm text-zinc-500">No media kit to preview.</p>}
          <p className="mt-2 text-center text-xs text-zinc-400">This is exactly what the business will see when you attach your media kit.</p>
        </DialogContent>
      </Dialog>

      <div className="nf-actionbar-abovenav border-t border-zinc-200 bg-white p-3 md:static md:mx-auto md:mt-4 md:max-w-3xl md:border-0 md:bg-transparent md:p-0 md:px-4">
        {!user ? (
          <Button data-testid="apply-login" onClick={() => navigate("/auth?mode=register&role=creator")} className="h-12 w-full rounded-full bg-emerald-600 text-base hover:bg-emerald-700">Sign up as a creator to apply</Button>
        ) : !isCreator ? (
          <p className="text-center text-sm text-zinc-500">Only creators can apply to opportunities.</p>
        ) : isInvited ? (
          <div className="flex gap-2 md:justify-end">
            <Button data-testid="decline-invite" variant="outline" disabled={busy} onClick={() => respondInvite("declined")} className="h-12 flex-1 rounded-full border-rose-200 text-rose-600 hover:bg-rose-50 md:flex-none md:px-6"><X className="mr-2 h-5 w-5" /> Decline</Button>
            <Button data-testid="accept-invite" disabled={busy} onClick={() => respondInvite("accepted")} className="h-12 flex-1 rounded-full bg-emerald-600 text-base hover:bg-emerald-700 md:flex-none md:px-8">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><CheckCircle2 className="mr-2 h-5 w-5" /> Accept invitation</>}</Button>
          </div>
        ) : hasApplied ? (
          o.opp_type === "event" && o.my_application_status === "accepted" ? (
            <Button disabled data-testid="attending-state" className="h-12 w-full rounded-full bg-fuchsia-100 text-base text-fuchsia-700"><CheckCircle2 className="mr-2 h-5 w-5" /> You're attending 🎉</Button>
          ) : o.opp_type === "event" && o.my_application_status === "waitlisted" ? (
            <Button disabled data-testid="waitlisted-state" className="h-12 w-full rounded-full bg-amber-100 text-base text-amber-700"><Clock className="mr-2 h-5 w-5" /> On the waitlist</Button>
          ) : o.my_application_status === "declined" ? (
            <Button disabled data-testid="not-selected-state" className="h-12 w-full rounded-full bg-zinc-100 text-base text-zinc-500"><CheckCircle2 className="mr-2 h-5 w-5" /> Not selected — see your dashboard</Button>
          ) : (
            <Button disabled data-testid="applied-state" className="h-12 w-full rounded-full bg-emerald-100 text-base text-emerald-700"><CheckCircle2 className="mr-2 h-5 w-5" /> Applied</Button>
          )
        ) : o.opp_type === "event" && (o.event_status === "cancelled" || o.event_status === "completed") ? (
          <Button disabled data-testid="event-closed-state" className="h-12 w-full rounded-full bg-zinc-100 text-base text-zinc-500">{o.event_status === "cancelled" ? "Event cancelled" : "Event completed"}</Button>
        ) : o.opp_type === "event" && o.is_full && !o.enable_waitlist ? (
          <Button disabled data-testid="event-full-state" className="h-12 w-full rounded-full bg-amber-100 text-base text-amber-700">Event full</Button>
        ) : o.opp_type === "event" ? (
          <Button data-testid="open-apply" onClick={() => setOpen(true)} className="h-12 w-full rounded-full bg-fuchsia-600 text-base hover:bg-fuchsia-700"><Ticket className="mr-2 h-5 w-5" /> {o.is_full ? "Join waitlist" : "Apply to attend"}</Button>
        ) : (
          <Button data-testid="open-apply" onClick={() => setOpen(true)} className="h-12 w-full rounded-full bg-emerald-600 text-base hover:bg-emerald-700"><Send className="mr-2 h-5 w-5" /> Apply for this opportunity</Button>
        )}
      </div>
      <MobileNav />
    </div>
  );
}
